import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Mail,
  Lock,
  Key,
  Eye,
  EyeOff,
  ArrowRight,
  KeyRound,
  TrendingUp,
  LogIn,
  ArrowLeft,
} from "lucide-react";
import API from "../services/api";
import { useToast } from "../components/Toast";
import {
  AUTH_LIMITS,
  hasValidEmailFormat,
  normalizeEmail,
  sanitizeAuthField,
} from "../utils/authValidation";
import "./Auth.css";

export default function ForgotPassword() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultEmail = sanitizeAuthField("email", searchParams.get("email") || "");
  const defaultCode = sanitizeAuthField("code", searchParams.get("code") || "");
  const defaultStep = searchParams.get("step") === "reset" && defaultEmail ? 2 : 1;

  const [step, setStep] = useState(defaultStep);
  const [form, setForm] = useState({
    email: defaultEmail,
    code: defaultCode,
    newPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: sanitizeAuthField(name, value) }));
  };

  const getValidatedEmail = () => {
    const email = normalizeEmail(form.email);

    if (!email) {
      toast.error("Please enter your email.");
      return null;
    }

    if (!hasValidEmailFormat(email)) {
      toast.error("Please enter a valid email address.");
      return null;
    }

    return email;
  };

  const sendOtp = async () => {
    const email = getValidatedEmail();
    if (!email) return;

    try {
      setLoading(true);
      await API.post("/auth/request-otp", {
        email,
        purpose: "forgot",
      });
      toast.success("Reset code sent to your email.");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to send reset code.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    const { code, newPassword } = form;
    const normalizedEmail = getValidatedEmail();

    if (!normalizedEmail) return;

    if (!code || !newPassword) {
      toast.error("Enter reset code and new password.");
      return;
    }

    if (code.length !== AUTH_LIMITS.resetCode) {
      toast.error("Reset code must be 6 digits.");
      return;
    }

    try {
      setLoading(true);
      await API.post("/auth/reset-password", {
        email: normalizedEmail,
        code,
        newPassword,
      });
      navigate("/login", {
        replace: true,
        state: {
          email: normalizedEmail,
          toast: {
            type: "success",
            message: "Password reset successful. Please log in.",
          },
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.msg || "Password reset failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-shell">
        <aside className="auth-aside">
          <div className="auth-aside-copy">
            <div className="auth-kicker">
              <KeyRound size={16} />
              Secure recovery
            </div>
            <h1>Recover access quickly.</h1>
            <p>Reset your password and get back to your analytics workspace.</p>

            <div className="auth-visual" aria-hidden="true">
              <lottie-player
                src="/trading.json"
                background="transparent"
                speed="1"
                loop
                autoplay
                className="auth-lottie"
              ></lottie-player>
            </div>
          </div>
        </aside>

        <section className="auth-panel">
          <div className="auth-panel-content">
            <div className="auth-mobile-mark" aria-hidden="true">
              <span className="auth-brand-mark">
                <TrendingUp size={24} />
              </span>
            </div>

            <div className="auth-header">
              <h2>{step === 1 ? "Reset Password" : "Create New Password"}</h2>
              <p>
                {step === 1
                  ? "Enter your email to receive a reset code."
                  : "Enter the reset code and choose a new password."}
              </p>
            </div>

            {step === 1 && (
              <>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendOtp();
                  }}
                  className="auth-form"
                >
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <div className="input-wrapper">
                      <Mail className="input-icon" />
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={onChange}
                        placeholder="Enter your email"
                        required
                        maxLength={AUTH_LIMITS.email}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <button type="submit" className="auth-button" disabled={loading}>
                    {loading ? (
                      <>
                        <div className="auth-loading-spinner"></div>
                        Sending Code...
                      </>
                    ) : (
                      <>
                        Send Reset Code
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>

                <div className="auth-links">
                  <div className="auth-divider">
                    <span>Remembered your password?</span>
                  </div>
                  <Link to="/login" className="signup-link">
                    <LogIn size={17} />
                    Sign In
                  </Link>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    resetPassword();
                  }}
                  className="auth-form"
                >
                  <div className="form-group">
                    <label htmlFor="code">Reset Code</label>
                    <div className="input-wrapper">
                      <Key className="input-icon" />
                      <input
                        id="code"
                        name="code"
                        type="text"
                        value={form.code}
                        onChange={onChange}
                        placeholder="Enter 6-digit code"
                        required
                        disabled={loading}
                        maxLength={AUTH_LIMITS.resetCode}
                        inputMode="numeric"
                        pattern={`[0-9]{${AUTH_LIMITS.resetCode}}`}
                        title="Please enter a 6-digit numeric reset code."
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <div className="input-wrapper">
                      <Lock className="input-icon" />
                      <input
                        id="newPassword"
                        name="newPassword"
                        type={showPassword ? "text" : "password"}
                        value={form.newPassword}
                        onChange={onChange}
                        placeholder="Enter new password"
                        required
                        maxLength={AUTH_LIMITS.password}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <button type="submit" className="auth-button" disabled={loading}>
                    {loading ? (
                      <>
                        <div className="auth-loading-spinner"></div>
                        Resetting Password...
                      </>
                    ) : (
                      <>
                        Reset Password
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>

                <div className="auth-links">
                  <button onClick={() => setStep(1)} className="auth-inline-action">
                    <ArrowLeft size={16} />
                    Back to Email
                  </button>

                  <div className="auth-divider">
                    <span>Ready to sign in?</span>
                  </div>

                  <Link to="/login" className="signup-link">
                    <LogIn size={17} />
                    Sign In
                  </Link>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
